import React from 'react';

/**
 * Top-level error boundary (2026-05-29). Before this, the app had NO
 * boundary anywhere — any render-time throw in the 15k-line App tree
 * unmounted everything and left a truly blank white page with no way
 * out but a manual refresh (and the user often didn't know to try).
 * This catches those throws and shows a branded recovery card with a
 * Reload button + a "Start over" that clears the stored session (covers
 * the case where a corrupt cached token/state is what's crashing render).
 *
 * Scope: this is the LAST-resort net. Data-fetch failures and the auth
 * bootstrap hang are handled inline in App (login screen fallbacks);
 * this only fires on an actual thrown exception during render/lifecycle.
 */
class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the console (and Sentry if the SDK is wired on window).
    // eslint-disable-next-line no-console
    console.error('[RootErrorBoundary] render crash:', error, info);
    try {
      if (window.Sentry && typeof window.Sentry.captureException === 'function') {
        window.Sentry.captureException(error, { extra: info });
      }
    } catch (_) { /* never let error reporting throw here */ }
  }

  render() {
    if (!this.state.error) return this.props.children;

    const wrap = {
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#0b0b14', color: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif', padding: 24,
    };
    const card = {
      maxWidth: 440, textAlign: 'center', background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '40px 36px',
    };
    const btn = (primary) => ({
      border: 'none', borderRadius: 10, padding: '11px 20px', fontSize: 14, fontWeight: 600,
      cursor: 'pointer', fontFamily: 'inherit',
      background: primary ? '#7c5cff' : 'rgba(255,255,255,0.08)',
      color: primary ? '#fff' : '#cbd5e1',
    });

    return (
      <div style={wrap}>
        <div style={card}>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}>Something went sideways</div>
          <div style={{ fontSize: 14, lineHeight: 1.55, color: '#cbd5e1', marginBottom: 24 }}>
            TrailerCast hit an unexpected error and couldn't finish loading this view.
            A reload usually clears it. If it keeps happening, "Start over" resets your
            local session.
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button type="button" style={btn(true)} onClick={() => window.location.reload()}>
              Reload
            </button>
            <button
              type="button"
              style={btn(false)}
              onClick={() => {
                try { localStorage.removeItem('trailercst_token'); } catch (_) { /* noop */ }
                window.location.href = '/';
              }}
            >
              Start over
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default RootErrorBoundary;
